import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AppUtilityService } from '../../services/app-utility.service';
import { FileUpload, FileUploadConfig } from '../../models/file-upload.model';
import { ReferenceFileInfo } from '../../models/run.model';
import { formatFileSize } from '../../utils/file-size.util';

/**
 * Shown when a reference upload fails with no message from the backend (e.g. a
 * status-0 "Unknown Error" from posting an acceptable filetype with invalid
 * content). Hedged so it also reads sensibly during a transient outage.
 */
export const UNRECOGNIZED_REF_FILE_MESSAGE =
  'Upload failed. Please confirm the file is a valid specification, calibrator lot, or control lot file. If it is valid, the service may be temporarily unavailable — please try again.';

@Component({
  selector: 'app-single-ref-file-upload',
  templateUrl: './single-ref-file-upload.component.html',
  styleUrl: './single-ref-file-upload.component.scss'
})
export class SingleRefFileUploadComponent implements OnInit, OnDestroy {

  @Input() config!: FileUploadConfig;

  @Output() referenceFileUploadResponse = new EventEmitter<ReferenceFileInfo>();

  @ViewChild('fileSelector', { static: false }) fileSelector!: ElementRef;

  selectedFile: FileUpload | null = null;
  comment = '';
  commentError = false;
  fileSelectionForm: FormGroup;

  /** Exposed for the template, which shows the staged file's size beside its name. */
  readonly formatFileSize = formatFileSize;

  private fileSelectionSub!: Subscription;

  constructor(private appUtilityService: AppUtilityService) {
    this.fileSelectionForm = new FormGroup({
      file_selection: new FormControl()
    });
  }

  ngOnInit(): void {
    const sub = this.fileSelectionForm.get('file_selection')?.valueChanges.subscribe(() => {
      const files: FileList = this.fileSelector.nativeElement.files;
      if (files?.length) {
        this.selectFile(files[0]);
      }
      this.fileSelector.nativeElement.value = '';
    });
    if (sub) {
      this.fileSelectionSub = sub;
    }
  }

  openFileSelector(): void {
    this.fileSelector.nativeElement.click();
  }

  selectFile(file: File): void {
    if (!this.config.mimeTypesAccepted || this.config.mimeTypesAccepted.indexOf(file.type) >= 0) {
      this.selectedFile = {
        file,
        isUploadInProgress: false,
        uploadResult: null,
        isPatient: false
      };
    } else {
      const message = 'Only files of the following types are allowed: ' + this.config.mimeTypesAccepted;
      this.appUtilityService.showSnackbar(message);
    }
  }

  onFilesDropped(files: FileList): void {
    if (files?.length) {
      this.selectFile(files[0]);
    }
  }

  uploadFile(): void {
    if (!this.selectedFile || this.selectedFile.isUploadInProgress || this.selectedFile.uploadResult === 'success') {
      return;
    }
    if (!this.comment.trim()) {
      this.commentError = true;
      return;
    }

    this.selectedFile.isUploadInProgress = true;
    this.selectedFile.uploadResult = null;

    const formData = new FormData();
    formData.append('file', this.selectedFile.file);
    formData.append('comment', this.comment.trim());

    this.appUtilityService.post<FormData, ReferenceFileInfo>(this.config.api, formData)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (this.selectedFile) {
            this.selectedFile.uploadResult = 'success';
            this.selectedFile.isUploadInProgress = false;
          }
          this.referenceFileUploadResponse.emit(response);
        },
        error: (err: unknown) => {
          const typedError = err as { error?: { detail?: string; message?: string } };
          const detail = typedError?.error?.detail || typedError?.error?.message;
          if (this.selectedFile) {
            this.selectedFile.uploadResult = detail || UNRECOGNIZED_REF_FILE_MESSAGE;
            this.selectedFile.isUploadInProgress = false;
          }
        }
      });
  }

  cancelFile(): void {
    this.selectedFile = null;
    this.comment = '';
    this.commentError = false;
  }

  onCommentChange(): void {
    this.commentError = false;
  }

  ngOnDestroy(): void {
    this.appUtilityService.unsubscribeAll([this.fileSelectionSub]);
  }
}
